import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import withApi from '../../components/higherOrderComponents/withApi';
import { formatCourtTime } from '../../common/copenhagenTime';

/**
 * Seasonal courts overview — courts down, times across, one facility and weekday
 * at a time.
 *
 * The cell colour is the whole page: it says *why* a slot is or is not sellable.
 * That classification is ported unchanged from the old page (which took it from
 * the standalone v0 overview), because the six cases are what somebody reads this
 * grid for, and quietly collapsing two of them would turn an anomaly into a
 * normal-looking cell:
 *
 *   available & on sale      — nothing to do
 *   available, not on sale   — sellable, nobody can buy it
 *   few slots left           — 1–2 left of the run
 *   occupied / full          — sold or taken
 *   not available, ON SALE   — an anomaly: on sale with nothing to sell
 *   no slot                  — no seasonal court at this court and time
 *
 * What changed is *how* the colour is applied: the old page carried six inline
 * style objects, so the grid stayed light-on-white in dark mode. Here the
 * classification returns a kind and the stylesheet colours it, which also means
 * the six cases are named in one place.
 *
 * **The anomalies are counted across everything, not just the visible grid.**
 * "Not available, ON SALE" is a slot somebody can buy and nobody can use — the
 * one case on this page that is a fault rather than a state. Finding one meant
 * stepping through every facility and every weekday looking for a colour, and
 * `getCourtsStatus` already answers the whole tree in a single request, so the
 * scan costs nothing. Each one is a button that takes the grid to it.
 */

// The status string is the API's own, and matching on it is what the old page
// does. Keep the tests in this order: "not available … not on sale" would also
// satisfy a looser check for "available".
function slotKind(slot) {
  if (!slot) return 'none';

  const status = (slot.status || '').toLowerCase();

  if (status.includes('not available') && status.includes('not on sale')) {
    // "[booked/available]" — 1 or 2 left of the run is worth flagging before it
    // sells out.
    const counts = status.match(/\[(\d+)\/(\d+)\]/);
    if (counts) {
      const available = parseInt(counts[2], 10);
      if (available > 0 && available <= 2) return 'few';
    }
  }

  if (status.includes('available') && status.includes('not on sale') && !status.includes('not available')) {
    return 'idle';
  }

  if (status.includes('not available') && status.includes('on sale') && !status.includes('not on sale')) {
    return 'anomaly';
  }

  switch ((slot.color || '').toLowerCase()) {
    case 'green': return 'ok';
    case 'red': return 'full';
    case 'yellow': return 'few';
    default: return 'plain';
  }
}

const LEGEND = [
  ['ok', 'Available & on sale'],
  ['idle', 'Available, not on sale'],
  ['few', 'Few slots left'],
  ['full', 'Occupied / full'],
  ['anomaly', 'Not available, ON SALE (anomaly)'],
  ['none', 'No slot']
];

const SeasonalOverviewInternal = ({ api }) => {
  const [facilities, setFacilities] = useState(null);
  const [error, setError] = useState(null);
  const [facility, setFacility] = useState('');
  const [day, setDay] = useState('');

  const load = useCallback(async () => {
    setFacilities(null);
    setError(null);
    try {
      const response = await api.seasonalcourts.getCourtsStatus();
      const data = (response && response.data) ? response.data : (response || []);
      const list = Array.isArray(data) ? data : [];
      setFacilities(list);

      // Land on something rather than an empty grid, and keep the current
      // selection across a refresh when it still exists.
      setFacility(previous => {
        const keep = list.some(item => item.facilityName === previous);
        return keep ? previous : (list[0]?.facilityName ?? '');
      });
    } catch (e) {
      setError(e?.message || 'Could not load the availability.');
      setFacilities([]);
    }
  }, [api]);

  useEffect(() => {
    load();
  }, [load]);

  const chosen = (facilities || []).find(item => item.facilityName === facility);
  const weekdays = chosen?.weekdays || [];

  // Follows the facility: its weekdays are its own, so a day held over from
  // another facility would show an empty grid that looks like missing data.
  useEffect(() => {
    if (weekdays.length === 0) {
      setDay('');
      return;
    }
    if (!weekdays.some(item => item.weekDay === day)) {
      setDay(weekdays[0].weekDay);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facility, facilities]);

  /**
   * Every anomaly in the payload, not only the ones on screen.
   *
   * Deliberately not a general "find me every colour": five of the six kinds are
   * ordinary states, and counting those would turn a fault list into a summary
   * nobody reads. This is the case that means something is wrong.
   */
  const anomalies = useMemo(() => (facilities || []).flatMap(item => (
    (item.weekdays || []).flatMap(weekday => (
      (weekday.courts || []).flatMap(court => (
        (court.slots || [])
          .filter(slot => slotKind(slot) === 'anomaly')
          .map(slot => ({
            facilityName: item.facilityName,
            weekDay: weekday.weekDay,
            courtName: court.courtName,
            time: slot.time
          }))
      ))
    ))
  )), [facilities]);

  const dayData = weekdays.find(item => item.weekDay === day);
  const courts = dayData?.courts || [];
  const times = dayData?.times || [];

  return (
    <div className="next-page next-page--wide">
      <header className="next-page__header next-page__header--row">
        <div>
          <h1>Seasonal courts overview</h1>
          <p className="next-muted">Seasonal availability by court and time slot.</p>
        </div>
      </header>

      {error && (
        <div className="next-note next-note--error">
          {error}{' '}
          <button type="button" className="next-btn next-btn--quiet" onClick={load}>Retry</button>
        </div>
      )}

      <section className="next-card">
        <div className="next-filters__row">
          <label className="next-filter">
            <span className="next-filter__label">Facility</span>
            <select
              className="next-input"
              value={facility}
              disabled={!facilities || facilities.length === 0}
              onChange={event => setFacility(event.target.value)}
            >
              {(facilities || []).map(item => (
                <option key={item.facilityName} value={item.facilityName}>{item.facilityName}</option>
              ))}
            </select>
          </label>

          <label className="next-filter">
            <span className="next-filter__label">Day</span>
            <select
              className="next-input"
              value={day}
              disabled={weekdays.length === 0}
              onChange={event => setDay(event.target.value)}
            >
              {weekdays.map(item => (
                <option key={item.weekDay} value={item.weekDay}>{item.weekDay}</option>
              ))}
            </select>
          </label>

          <div className="next-filters__buttons">
            <button type="button" className="next-btn" onClick={load}>Refresh</button>
          </div>
        </div>
      </section>

      {anomalies.length > 0 && (
        <div className="next-note next-note--warn">
          <strong>
            {anomalies.length} slot{anomalies.length === 1 ? ' is' : 's are'} on sale with nothing to sell
          </strong>
          {' — '}
          {new Set(anomalies.map(item => item.facilityName)).size} facilit
          {new Set(anomalies.map(item => item.facilityName)).size === 1 ? 'y' : 'ies'}.
          {' Somebody can buy '}
          {anomalies.length === 1 ? 'it' : 'them'} and nobody can use {anomalies.length === 1 ? 'it' : 'them'}.
          <div className="next-anomalies">
            {/* Capped: the point is to make them reachable, and a list of two
                hundred is a wall rather than a shortcut. The count above stays
                honest either way. */}
            {anomalies.slice(0, 12).map(item => (
              <button
                type="button"
                className="next-chip"
                key={`${item.facilityName}-${item.weekDay}-${item.courtName}-${item.time}`}
                onClick={() => { setFacility(item.facilityName); setDay(item.weekDay); }}
              >
                <span className="next-chip__label">{item.facilityName}</span>
                <span className="next-chip__value">
                  {item.weekDay} · {item.courtName} · {item.time}
                </span>
              </button>
            ))}
            {anomalies.length > 12 && (
              <span className="next-muted">and {anomalies.length - 12} more</span>
            )}
          </div>
        </div>
      )}

      <span className="next-legend next-legend--grid">
        {LEGEND.map(([kind, label]) => (
          <span className="next-legend__item" key={kind}>
            <i className={`next-slotkey next-slot--${kind}`} />
            {label}
          </span>
        ))}
      </span>

      <section className="next-card">
        {facilities === null ? (
          <p className="next-table__note next-muted">Loading…</p>
        ) : courts.length === 0 ? (
          <p className="next-table__note next-muted">
            {facilities.length === 0
              ? 'No facility has seasonal courts.'
              : 'No court for this facility on this day.'}
          </p>
        ) : (
          <div className="next-table-wrap">
            <table className="next-table next-grid">
              <thead>
                <tr>
                  <th>Court \ time (Copenhagen)</th>
                  {times.map(time => <th key={time} className="next-table__center">{formatCourtTime(time)}</th>)}
                </tr>
              </thead>
              <tbody>
                {courts.map(court => {
                  const byTime = {};
                  (court.slots || []).forEach(slot => { byTime[slot.time] = slot; });

                  return (
                    <tr key={court.courtName}>
                      <th scope="row" className="next-grid__court">{court.courtName}</th>
                      {times.map(time => {
                        const slot = byTime[time] || null;
                        const kind = slotKind(slot);
                        // The note is the fuller explanation; the status is what
                        // fits in the cell. Both come from the API.
                        const cell = (
                          <span
                            className={`next-slot next-slot--${kind}`}
                            title={slot ? (slot.note || slot.status || '') : 'No slot'}
                          >
                            {slot ? (slot.status || '') : ''}
                          </span>
                        );

                        return (
                          <td key={time} className="next-grid__cell">
                            {slot && slot.link
                              ? <a className="next-grid__link" href={slot.link}>{cell}</a>
                              : cell}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
};

SeasonalOverviewInternal.propTypes = {
  api: PropTypes.object.isRequired
};

const SeasonalOverview = withApi(SeasonalOverviewInternal);

SeasonalOverview.requiresLogin = true;

export default SeasonalOverview;
